/**
 * Экран регистрации `/register` (Фаза 3, И3).
 *
 * Форма `{username, fullName?, email, password, confirmPassword}` + обязательный
 * чекбокс согласия с [политикой конфиденциальности](/privacy) →
 * [`signUp`](../useAuth.tsx) (`POST /api/auth/register`). После успеха (201) аккаунт
 * создаётся залогиненным, но `verified:false` — показываем карточку «письмо
 * подтверждения отправлено на email» со ссылкой на главную (там же — неблокирующий
 * баннер «подтвердите email»).
 *
 * Кулдаун повторной отправки: сразу после регистрации (и после каждого успешного
 * ресенда) письмо только что отправлено, поэтому повторная отправка блокируется на
 * `RESEND_COOLDOWN_SECONDS` (60с) — на это время кнопка показывает обратный таймер.
 *
 * Anti-spam (скрытые, не подсвечиваются в UI):
 * - **Honeypot** `website` — скрытый `<input>` (смонтирован вне label/legend, с
 *   `display:none`/`tabIndex:-1`/`aria-hidden`); реальные пользователи его не
 *   заполняют. Передаётся в body, сервер фейково «успешно» отвечает ботам.
 * - **Timing** `ts` — `Date.now()` в момент mount формы; отправляется в body, сервер
 *   отклоняет сабмит быстрее ~2с (вероятный бот).
 *
 * Валидация (понятные ошибки, вкл. различимые 409 «ник занят»/«email занят»):
 * email-формат; пароль ≥ `MIN_PASSWORD_LENGTH` + совпадение; username
 * 3–32 руны, буквы/цифры/`_`/`-` (как на сервере, вкл. кириллицу); fullName ≤100 рун.
 *
 * `MIN_PASSWORD_LENGTH` экспортируется — переиспользуется экраном `Reset`.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, CAPTCHA_FAILED, CAPTCHA_REQUIRED, type CaptchaSolution } from '../api';
import { Captcha } from '../components/Captcha';
import { Link } from '../components/ui';
import { useAuth } from '../useAuth';

/** Минимальная длина пароля (совпадает с серверным `auth.ValidatePassword`). */
export const MIN_PASSWORD_LENGTH = 8;
/** Длина ника: 3–32 руны (совпадает с серверным `auth.ValidateUsername`). */
export const MIN_USERNAME_LENGTH = 3;
export const MAX_USERNAME_LENGTH = 32;
/** Максимум рун ФИО (совпадает с серверным `auth.MaxFullNameLen`). */
export const MAX_FULLNAME_LENGTH = 100;
/**
 * Кулдаун повторной отправки письма подтверждения (сек). Стартует сразу после
 * успешной регистрации (письмо только что ушло) и перезапускается после каждого
 * успешного ресенда — чтобы не спамить почту и сервер. Пока идёт отсчёт, кнопка
 * «Отправить письмо снова» заблокирована и показывает оставшиеся секунды.
 */
export const RESEND_COOLDOWN_SECONDS = 60;

/** Простой email-паттерн (финальная валидация — на сервере). */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/** Допустимые символы ника: буквы (unicode, вкл. кириллицу), цифры, `_`, `-`. */
const USERNAME_CHARS_RE = /^[\p{L}0-9_-]+$/u;

/**
 * Имя валидируемого поля регистрации (порядок значений — **сверху вниз** по форме).
 * Используется автофокусом П4 для выбора первого верхнего некорректного поля.
 */
type FieldName = 'username' | 'fullName' | 'email' | 'password' | 'confirm';

/** Число рун (code-point'ов) в строке — корректный лимит для UTF-8 (кириллица/эмодзи). */
function runeCount(s: string): number {
  return Array.from(s).length;
}

export interface RegisterProps {
  /** Вызывается после успешной регистрации (обычно — редирект на главную). */
  onSuccess?: () => void;
}

export function Register({ onSuccess }: RegisterProps): JSX.Element {
  const { t } = useTranslation();
  const { signUp, resendVerification } = useAuth();
  const [username, setUsername] = useState('');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Успешная регистрация: показываем карточку «письмо отправлено» вместо формы.
  const [doneEmail, setDoneEmail] = useState<string | null>(null);
  const [resendMsg, setResendMsg] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  // Кулдаун повторной отправки (сек, отсчитывает вниз до 0). Стартует с
  // RESEND_COOLDOWN_SECONDS в момент успешной регистрации и перезапускается после
  // каждого успешного ресенда. Пока > 0, кнопка «Отправить письмо снова»
  // заблокирована и показывает таймер.
  const [cooldown, setCooldown] = useState(0);
  useEffect(() => {
    if (cooldown <= 0) return;
    const id = window.setInterval(() => setCooldown((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => window.clearInterval(id);
  }, [cooldown]);

  // Honeypot (скрытое поле). Состояние нужно, чтобы value контролировалось
  // React'ом; всегда пустое у людей.
  const [website, setWebsite] = useState('');
  // Timing: метка открытия формы (mount). useRef, т.к. не влияет на рендер.
  const tsRef = useRef<number>(Date.now());

  // Слайд-капча (И8): решение от виджета + nonce для перевыпуска новой капчи при
  // ошибке (капча одноразовая — повторно отправить тот же captchaId нельзя, поэтому
  // после captcha_failed увеличиваем nonce и виджет монтируется заново → новый Issue).
  const [captcha, setCaptcha] = useState<CaptchaSolution | null>(null);
  const [captchaNonce, setCaptchaNonce] = useState(0);

  // П4 (Фаза 9): рефы на поля ввода — для автофокуса на первое верхнее некорректное
  // поле при неудачной отправке формы. Порядок ключей совпадает с `FieldName` и с
  // визуальным порядком полей (сверху вниз).
  const fieldRefs = {
    username: useRef<HTMLInputElement>(null),
    fullName: useRef<HTMLInputElement>(null),
    email: useRef<HTMLInputElement>(null),
    password: useRef<HTMLInputElement>(null),
    confirm: useRef<HTMLInputElement>(null),
  };
  // П4: имя первого верхнего инвалидного поля (управляет красной границей
  // `.input--invalid`). Сбрасывается при следующем изменении соответствующего поля
  // (см. `onChange` у input'ов) — граница исчезает, как только пользователь начал
  // править именно это поле. Красная рамка вешается только по сабмиту (не на каждый
  // keystroke), живая валидация (`shownError`) сохраняется.
  const [invalidField, setInvalidField] = useState<FieldName | null>(null);

  function getFieldError(): string | null {
    const un = username.trim();
    const unRunes = runeCount(un);
    if (unRunes < MIN_USERNAME_LENGTH)
      return t('errors.usernameTooShort', { min: MIN_USERNAME_LENGTH });
    if (unRunes > MAX_USERNAME_LENGTH)
      return t('errors.usernameTooLong', { max: MAX_USERNAME_LENGTH });
    if (!USERNAME_CHARS_RE.test(un)) return t('errors.usernameChars');
    const fn = fullName.trim();
    if (fn.length > 0 && runeCount(fn) > MAX_FULLNAME_LENGTH)
      return t('errors.fullNameTooLong', { max: MAX_FULLNAME_LENGTH });
    if (!EMAIL_RE.test(email.trim())) return t('errors.emailInvalid');
    if (password.length < MIN_PASSWORD_LENGTH)
      return t('errors.passwordTooShort', { min: MIN_PASSWORD_LENGTH });
    if (password !== confirm) return t('errors.passwordsMismatch');
    return null;
  }

  /**
   * Имя первого (сверху вниз по форме) невалидного поля или `null`. Порядок проверок
   * **совпадает с [`getFieldError`]** — это гарантирует, что автофокус и красная граница
   * попадают ровно на то поле, чьё сообщение показано в общем блоке `.error`. При
   * нескольких ошибках возвращается только первое верхнее (последующие подсвечиваются
   * по очереди при повторных отправках по мере исправления).
   */
  function firstInvalidField(): FieldName | null {
    const un = username.trim();
    const unRunes = runeCount(un);
    if (
      unRunes < MIN_USERNAME_LENGTH ||
      unRunes > MAX_USERNAME_LENGTH ||
      !USERNAME_CHARS_RE.test(un)
    ) {
      return 'username';
    }
    const fn = fullName.trim();
    if (fn.length > 0 && runeCount(fn) > MAX_FULLNAME_LENGTH) return 'fullName';
    if (!EMAIL_RE.test(email.trim())) return 'email';
    if (password.length < MIN_PASSWORD_LENGTH) return 'password';
    if (password !== confirm) return 'confirm';
    return null;
  }

  // Ошибки полей считаем один раз за рендер (чистая функция от state).
  const fieldError = getFieldError();
  // Кнопка отправки активна, как только отмечено согласие — ДАЖЕ при ошибках полей:
  // клик по ней с невалидными полями запускает handleSubmit → автофокус + красная
  // граница на первое верхнее некорректное поле + прокрутка (П4, Фаза 9). Раньше
  // кнопка была disabled при любой ошибке поля → handleSubmit был недостижим → фокус
  // и бордер никогда не срабатывали. Теперь гейт только на consent (обязательно для
  // сервера) + submitting (анти-двойной-сабмит).
  // Подсказки валидации показываем live, но только после того, как пользователь
  // отметил согласие (выразил намерение отправить) — иначе пустая форма сразу
  // ругалась бы. Серверная/submit-ошибка имеет приоритет над live-подсказкой.
  const shownError = error ?? (consent ? fieldError : null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (submitting) return;
    // П4: при ошибке валидации поднимаем фокус и прокрутку в первое верхнее некорректное
    // поле, подсвечиваем его красной границей. Текст ошибки (`getFieldError`) показывается
    // как раньше в общем блоке `.error` — логика сообщений и блокировка кнопки не меняются.
    const invalid = firstInvalidField();
    if (invalid) {
      setError(getFieldError());
      setInvalidField(invalid);
      const el = fieldRefs[invalid].current;
      el?.focus();
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setInvalidField(null);
    if (!consent) {
      setError(t('errors.consentRequired'));
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const u = await signUp({
        email: email.trim(),
        password,
        username: username.trim(),
        fullName: fullName.trim() || undefined,
        consent,
        website,
        ts: tsRef.current,
        // Слайд-капча (И8). Если виджет недоступен (CAPTCHA_ENABLED=0), captcha===undefined
        // и поля не уходят — сервер в no-op-режиме примет запрос без них.
        captchaId: captcha?.captchaId,
        captchaX: captcha?.captchaX,
      });
      setDoneEmail(u.email);
      // Письмо только что отправлено → запускаем кулдаун повторной отправки.
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (e) {
      // Капча одноразовая: после неуспеха перевыпускаем новую (remount по nonce) и
      // сбрасываем решение, иначе повторный сабмит ушёл бы с уже «сгоревшим» captchaId.
      // П5 (Фаза 9): remount также снимает зелёную рамку/✓ (новый компонент Captcha
      // стартует с solved=false) — пользователю явно сигнализируется: капчу нужно
      // пройти заново. Серверная ошибка капчи остаётся текстом в блоке `.error`.
      if (
        e instanceof ApiError &&
        (e.message === CAPTCHA_FAILED || e.message === CAPTCHA_REQUIRED)
      ) {
        setCaptchaNonce((n) => n + 1);
        setCaptcha(null);
      }
      setError(registerErrorMessage(t, e));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResend(): Promise<void> {
    // Кулдаун ещё идёт или отправка в полёте → повторная отправка запрещена
    // (кнопка также disabled, гард защищает от программного вызова).
    if (resending || cooldown > 0) return;
    setResending(true);
    setResendMsg(null);
    try {
      const res = await resendVerification();
      if (res.status === 'already verified') {
        setResendMsg(t('verify.alreadyVerified'));
      } else {
        setResendMsg(t('verify.resent'));
        // Письмо реально переотправлено → перезапускаем кулдаун, чтобы не спамить.
        setCooldown(RESEND_COOLDOWN_SECONDS);
      }
    } catch {
      setResendMsg(t('verify.resendFailed'));
    } finally {
      setResending(false);
    }
  }

  if (doneEmail !== null) {
    return (
      <main className="card card--page" aria-labelledby="register-done-title">
        <h1 id="register-done-title" className="page-title">
          {t('register.doneTitle')}
        </h1>
        <p className="muted" data-testid="register-success">
          {t('register.successIntro')} <strong>{doneEmail}</strong>. {t('register.successOutro')}
        </p>
        <p className="muted small">{t('register.successHint')}</p>
        <div className="btn-row">
          <button
            type="button"
            className="btn"
            onClick={() => void handleResend()}
            disabled={resending || cooldown > 0}
            data-testid="register-resend"
          >
            {resending
              ? t('common.submitting')
              : cooldown > 0
                ? t('register.resendIn', { seconds: cooldown })
                : t('common.resendEmail')}
          </button>
          <button
            type="button"
            className="cta"
            onClick={() => onSuccess?.()}
            data-testid="register-go-home"
          >
            {t('nav.toMain')}
          </button>
        </div>
        {resendMsg ? (
          <p className="field-hint" data-testid="register-resend-msg">
            {resendMsg}
          </p>
        ) : null}
      </main>
    );
  }

  return (
    <main className="card card--page" aria-labelledby="register-title">
      <h1 id="register-title" className="page-title">
        {t('register.title')}
      </h1>

      <form className="auth-form" onSubmit={handleSubmit} noValidate>
        {/* Honeypot: скрытое anti-spam-поле. НЕ оборачивать в label/legend; не
            показывать пользователю. Заполняется только ботами. */}
        <input
          name="website"
          style={{ display: 'none' }}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          data-testid="register-honeypot"
        />

        <div className="form-row">
          <label className="field-label" htmlFor="register-username">
            {t('register.username')}
          </label>
          <input
            id="register-username"
            className={`text-input${invalidField === 'username' ? ' input--invalid' : ''}`}
            type="text"
            autoComplete="username"
            value={username}
            ref={fieldRefs.username}
            onChange={(e) => {
              setUsername(e.target.value);
              setInvalidField((prev) => (prev === 'username' ? null : prev));
            }}
            placeholder="alice"
          />
          <span className="field-hint">
            {t('register.usernameHint', {
              min: MIN_USERNAME_LENGTH,
              max: MAX_USERNAME_LENGTH,
            })}
          </span>
        </div>

        <div className="form-row">
          <label className="field-label" htmlFor="register-fullname">
            {t('register.fullName')}
          </label>
          <input
            id="register-fullname"
            className={`text-input${invalidField === 'fullName' ? ' input--invalid' : ''}`}
            type="text"
            autoComplete="name"
            value={fullName}
            ref={fieldRefs.fullName}
            onChange={(e) => {
              setFullName(e.target.value);
              setInvalidField((prev) => (prev === 'fullName' ? null : prev));
            }}
          />
        </div>

        <div className="form-row">
          <label className="field-label" htmlFor="register-email">
            {t('common.email')}
          </label>
          <input
            id="register-email"
            className={`text-input${invalidField === 'email' ? ' input--invalid' : ''}`}
            type="email"
            autoComplete="email"
            value={email}
            ref={fieldRefs.email}
            onChange={(e) => {
              setEmail(e.target.value);
              setInvalidField((prev) => (prev === 'email' ? null : prev));
            }}
            placeholder="you@example.com"
          />
        </div>

        <div className="form-row">
          <label className="field-label" htmlFor="register-password">
            {t('common.password')}
          </label>
          <input
            id="register-password"
            className={`text-input${invalidField === 'password' ? ' input--invalid' : ''}`}
            type="password"
            autoComplete="new-password"
            value={password}
            ref={fieldRefs.password}
            onChange={(e) => {
              setPassword(e.target.value);
              setInvalidField((prev) => (prev === 'password' ? null : prev));
            }}
            placeholder="••••••••"
          />
          <span className="field-hint">
            {t('register.passwordHint', { min: MIN_PASSWORD_LENGTH })}
          </span>
        </div>

        <div className="form-row">
          <label className="field-label" htmlFor="register-confirm">
            {t('common.confirmPassword')}
          </label>
          <input
            id="register-confirm"
            className={`text-input${invalidField === 'confirm' ? ' input--invalid' : ''}`}
            type="password"
            autoComplete="new-password"
            value={confirm}
            ref={fieldRefs.confirm}
            onChange={(e) => {
              setConfirm(e.target.value);
              setInvalidField((prev) => (prev === 'confirm' ? null : prev));
            }}
            placeholder="••••••••"
          />
        </div>

        <div className="form-row form-row--check">
          <label className="consent-label">
            <input
              id="register-consent"
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              data-testid="register-consent"
            />
            <span>
              {t('register.consentPrefix')} <Link to="/privacy">{t('register.privacyLink')}</Link>.
            </span>
          </label>
        </div>

        {/* Слайд-капча (И8): обязательна перед отправкой, когда серверная капча
            включена. key=nonce → при серверном неуспехе (captchaNonce++) компонент
            размонтируется и монтируется заново: новая капча + сброс визуального
            состояния `solved` (П5: зелёная рамка/✓ снимаются при новой попытке). */}
        <Captcha key={captchaNonce} onChange={setCaptcha} />

        {shownError ? (
          <p className="error" role="alert" data-testid="register-error">
            {shownError}
          </p>
        ) : null}

        <button
          type="submit"
          className="cta"
          disabled={!consent || submitting}
          data-testid="register-submit"
        >
          {submitting ? t('register.creating') : t('register.submit')}
        </button>
      </form>

      <p className="auth-switch">
        {t('register.hasAccount')} <Link to="/login">{t('nav.login')}</Link>
      </p>
      <p className="auth-switch">
        <Link to="/">{t('nav.toMain')}</Link>
      </p>
    </main>
  );
}

/**
 * Дружелюбное сообщение по статусу ошибки регистрации. Различает 409 «email занят»
 * и «ник занят» (сервер присылает разные сообщения), 400 (вкл. consent/бот/валидация),
 * 429 (rate-limit) и сетевые сбои.
 */
function registerErrorMessage(
  t: (key: string, opts?: Record<string, unknown>) => string,
  e: unknown,
): string {
  if (e instanceof ApiError) {
    if (e.status === 409) {
      if (/username/i.test(e.message)) return t('errors.usernameTaken');
      return t('errors.emailTaken');
    }
    if (e.status === 400) {
      if (e.message === CAPTCHA_FAILED) return t('errors.captchaFailed');
      if (e.message === CAPTCHA_REQUIRED) return t('errors.captchaRequired');
      return e.message || t('errors.validation');
    }
    if (e.status === 429) return t('errors.registerRateLimited');
  }
  return t('errors.registerNetwork');
}
